/**
 * Gemini model catalogue for the AI settings guide (spec section 11) and the
 * generation lookup that decides which thinking options a model accepts.
 *
 * Pure data + pure functions; no SDK imports. The default model id lives in
 * `@/domain/settings/defaults` (single source of truth) and is re-used here.
 */
import { DEFAULT_GEMINI_MODEL } from '@/domain/settings/defaults';

export { DEFAULT_GEMINI_MODEL };

/** Generations of the curated Gemini models (shared contract). */
export type GeminiGeneration = '2.5' | '3.x';

/**
 * What `generationOf` can return for an arbitrary id typed under "Annan…":
 * a Gemini generation, or `'other'` for anything that is not a Gemini model
 * (Gemma, fine-tunes, typos). `'other'` models get no thinking options at
 * all, because e.g. Gemma rejects `thinkingConfig` with a 400.
 */
export type ModelGeneration = GeminiGeneration | 'other';

/** Thinking levels Gemini 3.x accepts as its floor; see `minimumThinkingLevelFor`. */
export type MinimumThinkingLevel = 'minimal' | 'low';

export interface GeminiModelOption {
  id: string;
  /** Swedish label shown in the model select. */
  label: string;
  /** Swedish one-line hint under the label. */
  hint: string;
  generation: GeminiGeneration;
}

/**
 * Curated options (research digest, Sept 2026). "Annan…" is a free-text entry,
 * see `OTHER_MODEL_ID`.
 *
 * 2.5 Flash-Lite is deliberately not offered: Google has closed it to new
 * users. Anyone who already had it keeps it — a stored id that is not listed
 * here still works, it just shows under "Annan…".
 */
export const GEMINI_MODELS: readonly GeminiModelOption[] = Object.freeze([
  { id: DEFAULT_GEMINI_MODEL, label: 'Gemini 3.5 Flash‑Lite', hint: 'Billigast, snabbast', generation: '3.x' },
  { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash‑Lite', hint: 'Bättre på suddiga kvitton', generation: '3.x' },
  { id: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', hint: 'Mest träffsäker', generation: '3.x' },
]);

/** Sentinel value for the "Annan…" select entry; the UI then shows a free-text model id field. */
export const OTHER_MODEL_ID = '__other__';
export const OTHER_MODEL_LABEL = 'Annan…';

export function isKnownGeminiModel(id: string): boolean {
  return GEMINI_MODELS.some((m) => m.id === id);
}

export function geminiModelLabel(id: string): string {
  return GEMINI_MODELS.find((m) => m.id === id)?.label ?? id;
}

/**
 * Strip the "models/" prefix Google uses in resource names. The Gemini SDK
 * builds its request path as `models/{id}:generateContent`, so an id that
 * already carries the prefix would be sent as `models/models/…` and 404 —
 * while a plain `GET /models/{id}` connection test, which strips it, passes.
 * Every id that reaches the provider goes through here.
 */
export function bareModelId(id: string): string {
  return id.trim().replace(/^models\//, '');
}

/** Last path segment, lower-cased: `models/gemini-2.5-flash` → `gemini-2.5-flash`. */
function bareLowerId(modelId: string): string {
  return (modelId.trim().split('/').at(-1) ?? '').toLowerCase();
}

/**
 * Which generation a model id belongs to. Drives `providerOptionsFor`:
 * 2.5 models take `thinkingBudget`, 3.x models take `thinkingLevel` and reject
 * `thinkingBudget` with a 400, and non-Gemini ids (`'other'`) take nothing.
 *
 * Unversioned Gemini aliases (`gemini-flash-latest`, `gemini-flash-lite-latest`)
 * are treated as 3.x, because every alias points at the current generation
 * and `thinkingLevel` is the forward-compatible option. The same regexes the
 * @ai-sdk/google provider uses (`/(^|\/)gemini-/`, `/(^|\/)gemini-2(?:[.-]|$)/`)
 * decide Gemini-vs-other and 2.x-vs-3.x, so our options agree with the SDK's.
 */
export function generationOf(modelId: string): ModelGeneration {
  const known = GEMINI_MODELS.find((m) => m.id === modelId);
  if (known) return known.generation;
  const id = bareLowerId(modelId);
  if (!/^gemini-/.test(id)) return 'other';
  const match = /^gemini-(\d+)(?:\.(\d+))?/.exec(id);
  if (!match) return '3.x';
  const major = Number(match[1]);
  return major >= 3 ? '3.x' : '2.5';
}

/**
 * The lowest `thinkingLevel` a Gemini 3.x model accepts. Full Flash models
 * from 3.7 on (and the unversioned `gemini-flash-latest`) reject `'minimal'`
 * and start at `'low'`; Flash-Lite, Pro and older Flash accept `'minimal'`.
 *
 * Mirrors `getMinimumThinkingLevelForGemini3Model` in @ai-sdk/google so the
 * explicit `thinkingConfig` we send never undercuts what the SDK would pick
 * from `reasoning: 'none'`.
 */
export function minimumThinkingLevelFor(modelId: string): MinimumThinkingLevel {
  const id = bareLowerId(modelId);
  if (id === 'gemini-flash-latest') return 'low';
  // `gemini-3.8-flash`, `gemini-3.8-flash-preview` match; `gemini-3.8-flash-lite` does not.
  const match = /^gemini-(\d+)\.(\d+)-flash(?:$|-(?!lite(?:-|$)))/.exec(id);
  if (!match) return 'minimal';
  const major = Number(match[1]);
  const minor = Number(match[2]);
  return major > 3 || (major === 3 && minor >= 7) ? 'low' : 'minimal';
}
